import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

import { getService } from "../../../../clients/feathers.clients";

const { Title } = Typography;
const { Option } = Select;

interface ProductGroup {
  id: number;
  name: string;
  description?: string;
  active: boolean;
}

interface ProductGroupProduct {
  id: number;
  product_group_id: number;
  product_id: number;
  product_group?: ProductGroup;
}

export const TableProductGroups = () => {
  const { id } = useParams<{ id: string }>();
  const productId = parseInt(id!);
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>();
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [productGroupProducts, setProductGroupProducts] = useState<
    ProductGroupProduct[]
  >([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingRelations, setLoadingRelations] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Load product groups
  const loadProductGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await getService("product-groups").find({
        query: {
          active: 1,
          $limit: 1000,
        },
      });
      setProductGroups(response.data || []);
    } catch (error) {
      console.error("Error loading product groups:", error);
      message.error("Error al cargar los grupos de productos");
    } finally {
      setLoadingGroups(false);
    }
  };

  // Load product group products for this product
  const loadProductGroupProducts = async () => {
    setLoadingRelations(true);
    try {
      const response = await getService("product-group-products").find({
        query: {
          product_id: productId,
          $limit: 1000,
        },
      });
      setProductGroupProducts(response.data || []);
    } catch (error) {
      console.error("Error loading product group products:", error);
      message.error("Error al cargar las relaciones de grupos");
    } finally {
      setLoadingRelations(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadProductGroups();
    loadProductGroupProducts();
  }, [productId]);

  // Add product to group
  const addToGroup = async (groupId: number) => {
    setAddLoading(true);
    try {
      await getService("product-group-products").create({
        product_group_id: groupId,
        product_id: productId,
      });
      message.success("Producto agregado al grupo exitosamente");
      loadProductGroupProducts(); // Reload relations
      setSelectedGroupId(undefined);
    } catch (error: any) {
      console.error("Error adding product to group:", error);
      message.error(error.message || "Error al agregar producto al grupo");
    } finally {
      setAddLoading(false);
    }
  };

  // Remove product from group
  const removeFromGroup = async (relationId: number) => {
    setRemoveLoading(true);
    try {
      await getService("product-group-products").remove(relationId);
      message.success("Producto removido del grupo exitosamente");
      loadProductGroupProducts(); // Reload relations
    } catch (error: any) {
      console.error("Error removing product from group:", error);
      message.error(error.message || "Error al remover producto del grupo");
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleAddToGroup = (groupId: number) => {
    addToGroup(groupId);
  };

  const handleRemoveFromGroup = (relationId: number) => {
    removeFromGroup(relationId);
  };

  // Get groups that the product is not already in
  const availableGroups = productGroups.filter(
    (group: ProductGroup) =>
      !productGroupProducts.some(
        (relation: ProductGroupProduct) =>
          relation.product_group_id === group.id
      )
  );

  // Debug logs
  console.log("productGroups:", productGroups);
  console.log("productGroupProducts:", productGroupProducts);
  console.log("availableGroups:", availableGroups);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Grupo",
      dataIndex: ["product_group", "name"],
      key: "group_name",
    },
    {
      title: "Descripción",
      dataIndex: ["product_group", "description"],
      key: "group_description",
      render: (description: string) => description || "-",
    },
    {
      title: "Estado",
      dataIndex: ["product_group", "active"],
      key: "group_active",
      width: 100,
      render: (active: boolean) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_: any, record: ProductGroupProduct) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveFromGroup(record.id)}
          loading={removeLoading}
        />
      ),
    },
  ];

  return (
    <Card>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={4}>Grupos de Productos</Title>
        </Col>

        {/* Add to group section */}
        <Col span={24}>
          <Card size="small" title="Agregar a Grupo">
            {productGroups.length === 0 ? (
              <div style={{ color: "#666", fontStyle: "italic" }}>
                No hay grupos de productos creados. Ve a "Grupos de Productos"
                para crear uno.
              </div>
            ) : availableGroups.length === 0 ? (
              <div style={{ color: "#666", fontStyle: "italic" }}>
                Este producto ya pertenece a todos los grupos disponibles.
              </div>
            ) : (
              <Space>
                <Select
                  placeholder="Seleccionar grupo"
                  style={{ width: 300 }}
                  loading={loadingGroups}
                  value={selectedGroupId}
                  onChange={setSelectedGroupId}
                >
                  {availableGroups.map((group: ProductGroup) => (
                    <Option key={group.id} value={group.id}>
                      {group.name}
                    </Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (selectedGroupId) {
                      handleAddToGroup(selectedGroupId);
                    }
                  }}
                  loading={addLoading}
                  disabled={!selectedGroupId}
                >
                  Agregar
                </Button>
              </Space>
            )}
          </Card>
        </Col>

        {/* Current groups table */}
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={productGroupProducts}
            loading={loadingRelations}
            rowKey="id"
            pagination={false}
            locale={{
              emptyText: "Este producto no pertenece a ningún grupo",
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};
